import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores';
import { useGuidedEntryStore } from '@/stores/guidedEntryStore';
import { useProducts } from '@/hooks/useProducts';
import { namesInitialProduct } from '@/pages/creatorInitialSelection';
import {
  resolveStartProcedure,
  type StartProcedure,
} from './guidedProductNotice';

/**
 * The run an embedded guided entry starts on, and whether the procedure bar
 * holds a placeholder until it is set.
 */
export function useGuidedStartProcedure(): { holdChoice: boolean } {
  const procedure = useGuidedEntryStore((state) => state.procedure);
  const setProcedure = useGuidedEntryStore((state) => state.setProcedure);

  // A marketplace iframe entry (isIframe=true, any channel) skips "Womit
  // startest du?" and starts on a run already selected — the bar still lets
  // the seller switch. The run is `?procedure=`, else the one the
  // preselected product supports, else Druck (resolveStartProcedure). Only
  // fills an *unset* choice of a new design: a loaded design's own run
  // (hydrateFromDesign) and a seller's own pick are never overridden.
  const isIframe = useAuthStore((state) => state.isIframe);
  const { designId } = useParams<{ designId?: string }>();
  const [searchParams] = useSearchParams();
  const preselection = useGuidedEntryStore((state) => state.preselection);
  const catalogQuery = useProducts();
  const start: StartProcedure | null =
    !isIframe || procedure !== null
      ? null
      : designId
        ? // A loaded design hydrates its own run asynchronously.
          'wait'
        : resolveStartProcedure({
            procedureParam: searchParams.get('procedure'),
            namesProduct: namesInitialProduct(searchParams),
            // Loaded data counts even when a later refetch failed.
            catalog: catalogQuery.data
              ? 'ready'
              : catalogQuery.isError
                ? 'error'
                : 'loading',
            products: catalogQuery.data,
            preselection,
          });
  // CreatorPage resets the store in the same commit as the effect below first
  // runs. The run set there never reaches a render then, so neither `start`
  // nor `procedure` changes and the effect would not run again. A reset
  // therefore asks for a fresh render, whose start is derived from the store
  // as the reset left it (a stale preselection included, which it clears).
  const [resetCount, setResetCount] = useState(0);
  useEffect(() => {
    if (start !== 'print' && start !== 'embroidery') return;
    // Re-read: a pick made since this render wins over the start.
    if (useGuidedEntryStore.getState().procedure === null) {
      setProcedure(start);
    }
    return useGuidedEntryStore.subscribe((state, previous) => {
      if (state.procedure === null && previous.procedure !== null) {
        setResetCount((count) => count + 1);
      }
    });
  }, [start, resetCount, setProcedure]);
  // Until the start is set, a placeholder in the bar's shape stands where the
  // hero would be, so the hero never flashes up only to morph into the bar.
  const holdChoice = start !== null && start !== 'none';
  return { holdChoice };
}
